import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { API_URL } from '../config/api';
import './SettingsManager.css';
import DesignManager from './DesignManager';

const SettingsManager = () => {
  const [activeTab, setActiveTab] = useState('basic');
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    companyName: '',
    logo: '',
    websiteUrl: '',
    email: '',
    supportEmail: '',
    whatsappNumber: '',
    whatsappLink: '',
    address: {
      street: '',
      city: '',
      state: '',
      zipCode: '',
      country: ''
    },
    businessHours: {
      weekdays: '',
      weekend: ''
    },
    instagramUrl: '',
    facebookUrl: '',
    twitterUrl: '',
    linkedinUrl: '',
    ourStory: {
      paragraph1: '',
      paragraph2: ''
    },
    statistics: {
      happyCustomers: '',
      productsDelivered: '',
      citiesServed: '',
      yearsOfExcellence: ''
    },
    joinOurJourney: {
      heading: '',
      description: ''
    },
    paymentApi: {
      razorpayKeyId: '',
      razorpayKeySecret: '',
      razorpayMode: 'test'
    },
    smtp: {
      enabled: false,
      host: 'smtp.gmail.com',
      port: 587,
      secure: false,
      user: '',
      password: '',
      fromEmail: '',
      fromName: 'Shilpani'
    },
    shipping: {
      originAddress: {
        street: '',
        city: '',
        state: '',
        zipCode: '',
        country: 'India'
      },
      pricingRules: {
        basePrice: 100,
        freeShippingThreshold: 5000,
        weightBased: {
          enabled: false,
          pricePerKg: 50,
          maxWeight: 50
        },
        sizeBased: {
          enabled: false,
          tiers: []
        },
        distanceBased: {
          enabled: false,
          pricePerKm: 2
        }
      },
      information: {
        estimatedDelivery: '5-7 business days',
        processingTime: '1-2 business days',
        shippingMethods: 'Standard Shipping, Express Shipping',
        returnPolicy: '7 days return policy',
        careInstructions: 'Handle with care. Keep away from moisture.'
      }
    }
  });

  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/settings`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = response.data.data;
      // If key secret is masked, keep the existing value in form (don't overwrite with masked value)
      if (data.paymentApi?.razorpayKeySecret?.startsWith('••••')) {
        // Keep existing value if it's already set, otherwise use empty
        if (!formData.paymentApi?.razorpayKeySecret || formData.paymentApi.razorpayKeySecret.startsWith('••••')) {
          data.paymentApi.razorpayKeySecret = '';
        } else {
          data.paymentApi.razorpayKeySecret = formData.paymentApi.razorpayKeySecret;
        }
      }
      // If SMTP password is masked, keep the existing value in form
      if (data.smtp?.password?.startsWith('••••')) {
        if (!formData.smtp?.password || formData.smtp.password.startsWith('••••')) {
          data.smtp.password = '';
        } else {
          data.smtp.password = formData.smtp.password;
        }
      }
      setFormData(data);
    } catch (error) {
      toast.error('Failed to fetch settings');
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    const uploadFormData = new FormData();
    uploadFormData.append('image', file);

    try {
      const response = await axios.post(`${API_URL}/api/upload?uploadType=settings`, uploadFormData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${token}`,
        },
      });

      setFormData({ ...formData, logo: response.data.data.baseS3Url || response.data.data.url });
      toast.success('Logo uploaded successfully!');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Logo upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleAddressChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      address: { ...formData.address, [name]: value }
    });
  };

  const handleBusinessHoursChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      businessHours: { ...formData.businessHours, [name]: value }
    });
  };

  const handleOurStoryChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      ourStory: { ...formData.ourStory, [name]: value }
    });
  };

  const handleStatisticsChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      statistics: { ...formData.statistics, [name]: value }
    });
  };

  const handleJoinOurJourneyChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      joinOurJourney: { ...formData.joinOurJourney, [name]: value }
    });
  };

  const handlePaymentApiChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      paymentApi: { ...formData.paymentApi, [name]: value }
    });
  };

  const handleSmtpChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      smtp: { 
        ...formData.smtp, 
        [name]: type === 'checkbox' ? checked : (type === 'number' ? parseInt(value) || 0 : value)
      }
    });
  };

  const handleShippingOriginAddressChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      shipping: {
        ...formData.shipping,
        originAddress: { ...formData.shipping.originAddress, [name]: value }
      }
    });
  };

  const handleShippingPricingChange = (e) => {
    const { name, value, type, checked } = e.target;
    const fieldPath = name.split('.');
    
    if (fieldPath.length === 2) {
      // e.g., basePrice, freeShippingThreshold
      setFormData({
        ...formData,
        shipping: {
          ...formData.shipping,
          pricingRules: {
            ...formData.shipping.pricingRules,
            [name]: type === 'number' ? parseFloat(value) || 0 : value
          }
        }
      });
    } else if (fieldPath.length === 3) {
      // e.g., weightBased.enabled, weightBased.pricePerKg
      const [parent, child] = fieldPath;
      setFormData({
        ...formData,
        shipping: {
          ...formData.shipping,
          pricingRules: {
            ...formData.shipping.pricingRules,
            [parent]: {
              ...formData.shipping.pricingRules[parent],
              [child]: type === 'checkbox' ? checked : (type === 'number' ? parseFloat(value) || 0 : value)
            }
          }
        }
      });
    }
  };

  const handleShippingInfoChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      shipping: {
        ...formData.shipping,
        information: { ...formData.shipping.information, [name]: value }
      }
    });
  };

  const handleSizeTierAdd = () => {
    setFormData({
      ...formData,
      shipping: {
        ...formData.shipping,
        pricingRules: {
          ...formData.shipping.pricingRules,
          sizeBased: {
            ...formData.shipping.pricingRules.sizeBased,
            tiers: [
              ...formData.shipping.pricingRules.sizeBased.tiers,
              { maxDimension: 0, price: 0 }
            ]
          }
        }
      }
    });
  };

  const handleSizeTierChange = (index, field, value) => {
    const newTiers = [...formData.shipping.pricingRules.sizeBased.tiers];
    newTiers[index][field] = parseFloat(value) || 0;
    setFormData({
      ...formData,
      shipping: {
        ...formData.shipping,
        pricingRules: {
          ...formData.shipping.pricingRules,
          sizeBased: {
            ...formData.shipping.pricingRules.sizeBased,
            tiers: newTiers
          }
        }
      }
    });
  };

  const handleSizeTierRemove = (index) => {
    const newTiers = formData.shipping.pricingRules.sizeBased.tiers.filter((_, i) => i !== index);
    setFormData({
      ...formData,
      shipping: {
        ...formData.shipping,
        pricingRules: {
          ...formData.shipping.pricingRules,
          sizeBased: {
            ...formData.shipping.pricingRules.sizeBased,
            tiers: newTiers
          }
        }
      }
    });
  };

  const handleSubmit = async (e) => {
    // Prevent any default form behavior and event bubbling
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Authentication required. Please login again.');
        return false;
      }
      
      // Prepare data - don't send key secret if it's masked or empty (means user didn't change it)
      const submitData = { ...formData };
      if (submitData.paymentApi?.razorpayKeySecret) {
        // If key secret starts with ••••, it means it's masked from server, so don't send it
        if (submitData.paymentApi.razorpayKeySecret.startsWith('••••')) {
          delete submitData.paymentApi.razorpayKeySecret;
        }
      }
      // Don't send SMTP password if it's masked or empty
      if (submitData.smtp?.password) {
        if (submitData.smtp.password.startsWith('••••')) {
          delete submitData.smtp.password;
        }
      }
      
      await axios.put(`${API_URL}/api/settings`, submitData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Settings updated successfully!');
      // Refresh settings to get updated data
      fetchSettings();
    } catch (error) {
      console.error('Settings submit error:', error);
      // Handle authentication errors specifically
      if (error.response?.status === 401) {
        toast.error('Authentication failed. Please login again.');
      } else {
        toast.error(error.response?.data?.message || 'Failed to update settings');
      }
      return false;
    }
  };

  if (loading) {
    return <div className="settings-manager"><p>Loading settings...</p></div>;
  }

  return (
    <div className="settings-manager">
      <div className="header">
        <h2>Settings</h2>
      </div>

      {/* Tabs */}
      <div className="tabs">
        <button
          type="button"
          className={`tab ${activeTab === 'basic' ? 'active' : ''}`}
          onClick={() => setActiveTab('basic')}
        >
          Basic Info
        </button>
        <button
          type="button"
          className={`tab ${activeTab === 'contact' ? 'active' : ''}`}
          onClick={() => setActiveTab('contact')}
        >
          Contact
        </button>
        <button
          type="button"
          className={`tab ${activeTab === 'address' ? 'active' : ''}`}
          onClick={() => setActiveTab('address')}
        >
          Address
        </button>
        <button
          type="button"
          className={`tab ${activeTab === 'about' ? 'active' : ''}`}
          onClick={() => setActiveTab('about')}
        >
          About
        </button>
        <button
          type="button"
          className={`tab ${activeTab === 'payment' ? 'active' : ''}`}
          onClick={() => setActiveTab('payment')}
        >
          Payment API
        </button>
        <button
          type="button"
          className={`tab ${activeTab === 'shipping' ? 'active' : ''}`}
          onClick={() => setActiveTab('shipping')}
        >
          Shipping
        </button>
        <button
          type="button"
          className={`tab ${activeTab === 'smtp' ? 'active' : ''}`}
          onClick={() => setActiveTab('smtp')}
        >
          Email/SMTP
        </button>
        <button
          type="button"
          className={`tab ${activeTab === 'themes' ? 'active' : ''}`}
          onClick={() => setActiveTab('themes')}
        >
          Themes
        </button>
        <button
          type="button"
          className={`tab ${activeTab === 'designs' ? 'active' : ''}`}
          onClick={() => setActiveTab('designs')}
        >
          Designs
        </button>
      </div>
      
      <form 
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          handleSubmit(e);
        }} 
        className="settings-form"
        noValidate
      >
        {/* Basic Info Tab */}
        {activeTab === 'basic' && (
          <div className="tab-content">
            <div className="form-section">
              <h3>Company Information</h3>
              
              <div className="form-group">
                <label>Company Name *</label>
                <input
                  type="text"
                  name="companyName"
                  value={formData.companyName}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="form-group">
                <label>Logo</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  disabled={uploading}
                />
                {uploading && <p>Uploading...</p>}
                {formData.logo && (
                  <div className="image-preview">
                    <img src={formData.logo} alt="Logo" />
                  </div>
                )}
              </div>

              <div className="form-group">
                <label>Website URL</label>
                <input
                  type="url"
                  name="websiteUrl"
                  value={formData.websiteUrl}
                  onChange={handleInputChange}
                  placeholder="https://shilpani.com"
                />
              </div>
            </div>

            <div className="form-section">
              <h3>Business Hours</h3>
              
              <div className="form-group">
                <label>Weekdays</label>
                <input
                  type="text"
                  name="weekdays"
                  value={formData.businessHours.weekdays}
                  onChange={handleBusinessHoursChange}
                  placeholder="Monday - Saturday: 10 AM - 7 PM"
                />
              </div>

              <div className="form-group">
                <label>Weekend</label>
                <input
                  type="text"
                  name="weekend"
                  value={formData.businessHours.weekend}
                  onChange={handleBusinessHoursChange}
                  placeholder="Sunday: 11 AM - 5 PM"
                />
              </div>
            </div>

            <div className="form-section">
              <h3>Social Media Links</h3>
              
              <div className="form-group">
                <label>Instagram URL</label>
                <input
                  type="url"
                  name="instagramUrl"
                  value={formData.instagramUrl}
                  onChange={handleInputChange}
                  placeholder="https://instagram.com/yourhandle"
                />
              </div>

              <div className="form-group">
                <label>Facebook URL</label>
                <input
                  type="url"
                  name="facebookUrl"
                  value={formData.facebookUrl}
                  onChange={handleInputChange}
                  placeholder="https://facebook.com/yourpage"
                />
              </div>

              <div className="form-group">
                <label>Twitter URL</label>
                <input
                  type="url"
                  name="twitterUrl"
                  value={formData.twitterUrl}
                  onChange={handleInputChange}
                  placeholder="https://twitter.com/yourhandle"
                />
              </div>

              <div className="form-group">
                <label>LinkedIn URL</label>
                <input
                  type="url"
                  name="linkedinUrl"
                  value={formData.linkedinUrl}
                  onChange={handleInputChange}
                  placeholder="https://linkedin.com/company/yourcompany"
                />
              </div>
            </div>
          </div>
        )}

        {/* Contact Tab */}
        {activeTab === 'contact' && (
          <div className="tab-content">
            <div className="form-section">
              <h3>Contact Information</h3>
              
              <div className="form-row">
                <div className="form-group">
                  <label>Email *</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Support Email</label>
                  <input
                    type="email"
                    name="supportEmail"
                    value={formData.supportEmail}
                    onChange={handleInputChange}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>WhatsApp Number *</label>
                  <input
                    type="text"
                    name="whatsappNumber"
                    value={formData.whatsappNumber}
                    onChange={handleInputChange}
                    required
                    placeholder="+91 98765 43210"
                  />
                </div>

                <div className="form-group">
                  <label>WhatsApp Link *</label>
                  <input
                    type="url"
                    name="whatsappLink"
                    value={formData.whatsappLink}
                    onChange={handleInputChange}
                    required
                    placeholder="https://wa.me/919876543210"
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Address Tab */}
        {activeTab === 'address' && (
          <div className="tab-content">
            <div className="form-section">
              <h3>Address</h3>
              
              <div className="form-group">
                <label>Street Address</label>
                <input
                  type="text"
                  name="street"
                  value={formData.address.street}
                  onChange={handleAddressChange}
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>City</label>
                  <input
                    type="text"
                    name="city"
                    value={formData.address.city}
                    onChange={handleAddressChange}
                  />
                </div>

                <div className="form-group">
                  <label>State</label>
                  <input
                    type="text"
                    name="state"
                    value={formData.address.state}
                    onChange={handleAddressChange}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Zip Code</label>
                  <input
                    type="text"
                    name="zipCode"
                    value={formData.address.zipCode}
                    onChange={handleAddressChange}
                  />
                </div>

                <div className="form-group">
                  <label>Country</label>
                  <input
                    type="text"
                    name="country"
                    value={formData.address.country}
                    onChange={handleAddressChange}
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* About Tab */}
        {activeTab === 'about' && (
          <div className="tab-content">
            <div className="form-section">
              <h3>Our Story</h3>
              
              <div className="form-group">
                <label>Paragraph 1</label>
                <textarea
                  name="paragraph1"
                  value={formData.ourStory.paragraph1}
                  onChange={handleOurStoryChange}
                  rows="4"
                  placeholder="First paragraph of your story..."
                />
              </div>

              <div className="form-group">
                <label>Paragraph 2</label>
                <textarea
                  name="paragraph2"
                  value={formData.ourStory.paragraph2}
                  onChange={handleOurStoryChange}
                  rows="4"
                  placeholder="Second paragraph of your story..."
                />
              </div>
            </div>

            <div className="form-section">
              <h3>Statistics</h3>
              
              <div className="form-row">
                <div className="form-group">
                  <label>Happy Customers</label>
                  <input
                    type="text"
                    name="happyCustomers"
                    value={formData.statistics.happyCustomers}
                    onChange={handleStatisticsChange}
                    placeholder="5000+"
                  />
                </div>

                <div className="form-group">
                  <label>Products Delivered</label>
                  <input
                    type="text"
                    name="productsDelivered"
                    value={formData.statistics.productsDelivered}
                    onChange={handleStatisticsChange}
                    placeholder="1000+"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Cities Served</label>
                  <input
                    type="text"
                    name="citiesServed"
                    value={formData.statistics.citiesServed}
                    onChange={handleStatisticsChange}
                    placeholder="50+"
                  />
                </div>

                <div className="form-group">
                  <label>Years of Excellence</label>
                  <input
                    type="text"
                    name="yearsOfExcellence"
                    value={formData.statistics.yearsOfExcellence}
                    onChange={handleStatisticsChange}
                    placeholder="5"
                  />
                </div>
              </div>
            </div>

            <div className="form-section">
              <h3>Join Our Journey</h3>
              
              <div className="form-group">
                <label>Heading</label>
                <input
                  type="text"
                  name="heading"
                  value={formData.joinOurJourney.heading}
                  onChange={handleJoinOurJourneyChange}
                  placeholder="Join Our Journey"
                />
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea
                  name="description"
                  value={formData.joinOurJourney.description}
                  onChange={handleJoinOurJourneyChange}
                  rows="3"
                  placeholder="Description text..."
                />
              </div>
            </div>
          </div>
        )}

        {/* Payment API Tab */}
        {activeTab === 'payment' && (
          <div className="tab-content">
            <div className="form-section">
              <div className="section-header">
                <h3>💳 Payment Gateway Configuration</h3>
                <p className="section-description">
                  Configure Razorpay payment gateway to enable secure payment processing for workshops and orders.
                </p>
              </div>

              {/* Status Banner */}
              <div className={`status-banner ${formData.paymentApi?.razorpayKeyId && formData.paymentApi?.razorpayKeySecret ? 'success' : 'warning'}`}>
                {formData.paymentApi?.razorpayKeyId && formData.paymentApi?.razorpayKeySecret ? (
                  <>
                    <span className="status-icon">✓</span>
                    <div>
                      <strong>Payment Gateway Active</strong>
                      <p>Razorpay is configured in <strong>{formData.paymentApi?.razorpayMode === 'live' ? 'Live' : 'Test'}</strong> mode. 
                      {formData.paymentApi?.razorpayMode === 'test' && ' Use Live Mode for real transactions in production.'}</p>
                    </div>
                  </>
                ) : (
                  <>
                    <span className="status-icon">⚠</span>
                    <div>
                      <strong>Payment Gateway Not Configured</strong>
                      <p>Please configure Razorpay credentials below to enable payment processing.</p>
                    </div>
                  </>
                )}
              </div>

              {/* Razorpay Configuration */}
              <div className="payment-gateway-section">
                <h4>🔑 Razorpay API Credentials</h4>
                
                <div className="form-group">
                  <label htmlFor="razorpayKeyId">
                    Razorpay Key ID <span className="required">*</span>
                  </label>
                  <input
                    id="razorpayKeyId"
                    type="text"
                    name="razorpayKeyId"
                    value={formData.paymentApi?.razorpayKeyId || ''}
                    onChange={handlePaymentApiChange}
                    placeholder="rzp_test_xxxxxxxxxxxxx or rzp_live_xxxxxxxxxxxxx"
                    className={formData.paymentApi?.razorpayKeyId ? 'has-value' : ''}
                  />
                  <small>
                    Your Razorpay Key ID (starts with <code>rzp_test_</code> for test mode or <code>rzp_live_</code> for live mode)
                  </small>
                </div>

                <div className="form-group">
                  <label htmlFor="razorpayKeySecret">
                    Razorpay Key Secret <span className="required">*</span>
                  </label>
                  <div className="input-with-toggle">
                    <input
                      id="razorpayKeySecret"
                      type="password"
                      name="razorpayKeySecret"
                      value={formData.paymentApi?.razorpayKeySecret || ''}
                      onChange={handlePaymentApiChange}
                      placeholder={formData.paymentApi?.razorpayKeySecret?.startsWith('••••') ? '••••••••' + formData.paymentApi.razorpayKeySecret.slice(-4) : 'Enter your Razorpay Key Secret'}
                      className={formData.paymentApi?.razorpayKeySecret && !formData.paymentApi.razorpayKeySecret.startsWith('••••') ? 'has-value' : ''}
                    />
                    {formData.paymentApi?.razorpayKeySecret?.startsWith('••••') && (
                      <span className="masked-indicator">Masked - Leave unchanged or enter new secret</span>
                    )}
                  </div>
                  <small>
                    Your Razorpay Key Secret (keep this secure and never share publicly)
                  </small>
                </div>

                <div className="form-group">
                  <label htmlFor="razorpayMode">Payment Mode</label>
                  <select
                    id="razorpayMode"
                    name="razorpayMode"
                    value={formData.paymentApi?.razorpayMode || 'test'}
                    onChange={handlePaymentApiChange}
                    className={formData.paymentApi?.razorpayMode === 'live' ? 'live-mode' : 'test-mode'}
                  >
                    <option value="test">🧪 Test Mode (Development)</option>
                    <option value="live">🚀 Live Mode (Production)</option>
                  </select>
                  <small>
                    {formData.paymentApi?.razorpayMode === 'test' ? (
                      <>Use <strong>Test Mode</strong> for development and testing. Test payments won't be charged.</>
                    ) : (
                      <>Use <strong>Live Mode</strong> for production. Real payments will be processed.</>
                    )}
                  </small>
                </div>
              </div>

              {/* Integration Info */}
              <div className="info-box payment-info">
                <h4>📋 How to Configure Razorpay:</h4>
                <div className="steps-list">
                  <div className="step-item">
                    <span className="step-number">1</span>
                    <div>
                      <strong>Create/Login to Razorpay Account</strong>
                      <p>Visit <a href="https://dashboard.razorpay.com" target="_blank" rel="noopener noreferrer">Razorpay Dashboard</a> and sign up or log in</p>
                    </div>
                  </div>
                  <div className="step-item">
                    <span className="step-number">2</span>
                    <div>
                      <strong>Get API Keys</strong>
                      <p>Navigate to <strong>Settings → API Keys</strong> in your Razorpay dashboard</p>
                    </div>
                  </div>
                  <div className="step-item">
                    <span className="step-number">3</span>
                    <div>
                      <strong>Generate Keys</strong>
                      <p>Generate API keys:
                        <ul>
                          <li><strong>Test Keys:</strong> For development and testing (no real charges)</li>
                          <li><strong>Live Keys:</strong> For production (real payments)</li>
                        </ul>
                      </p>
                    </div>
                  </div>
                  <div className="step-item">
                    <span className="step-number">4</span>
                    <div>
                      <strong>Copy & Paste</strong>
                      <p>Copy the Key ID and Key Secret, then paste them in the fields above</p>
                    </div>
                  </div>
                  <div className="step-item">
                    <span className="step-number">5</span>
                    <div>
                      <strong>Select Mode & Save</strong>
                      <p>Choose Test or Live mode, then click "Save Settings"</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* What's Integrated */}
              <div className="info-box integration-info">
                <h4>🔗 What's Integrated:</h4>
                <ul>
                  <li>✅ <strong>Workshop Bookings:</strong> Users can pay for workshops securely</li>
                  <li>✅ <strong>Product Orders:</strong> Payment processing for product purchases</li>
                  <li>✅ <strong>Payment Verification:</strong> Automatic payment verification and status updates</li>
                  <li>✅ <strong>Secure Transactions:</strong> All payments are processed securely through Razorpay</li>
                </ul>
              </div>

              {/* Security Warning */}
              <div className="warning-box">
                <h4>🔒 Security Best Practices:</h4>
                <ul>
                  <li>⚠️ Never share your Key Secret publicly or commit it to version control</li>
                  <li>⚠️ Use Test Mode keys during development</li>
                  <li>⚠️ Only use Live Mode keys in production environment</li>
                  <li>⚠️ If your Key Secret is compromised, regenerate it immediately from Razorpay Dashboard</li>
                  <li>⚠️ The Key Secret is masked in the database and only shown partially in admin panel</li>
                </ul>
              </div>

              {/* Test Payment Info */}
              {formData.paymentApi?.razorpayMode === 'test' && formData.paymentApi?.razorpayKeyId && (
                <div className="info-box test-payment-info">
                  <h4>🧪 Test Payment Cards:</h4>
                  <p>Use these test cards in Test Mode (from <a href="https://razorpay.com/docs/payments/test-card-numbers/" target="_blank" rel="noopener noreferrer">Razorpay Documentation</a>):</p>
                  <table className="test-cards-table">
                    <thead>
                      <tr>
                        <th>Card Number</th>
                        <th>CVV</th>
                        <th>Expiry</th>
                        <th>Result</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td><code>4111 1111 1111 1111</code></td>
                        <td>Any</td>
                        <td>Any future date</td>
                        <td>✅ Success</td>
                      </tr>
                      <tr>
                        <td><code>5555 5555 5555 4444</code></td>
                        <td>Any</td>
                        <td>Any future date</td>
                        <td>✅ Success</td>
                      </tr>
                      <tr>
                        <td><code>4000 0000 0000 0002</code></td>
                        <td>Any</td>
                        <td>Any future date</td>
                        <td>❌ Failed</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* SMTP Email Tab */}
        {activeTab === 'smtp' && (
          <div className="tab-content">
            <div className="form-section">
              <div className="section-header">
                <h3>📧 Email/SMTP Configuration</h3>
                <p className="section-description">
                  Configure SMTP settings to send automated emails for order confirmations and account creation.
                </p>
              </div>

              {/* Status Banner */}
              <div className={`status-banner ${formData.smtp?.enabled && formData.smtp?.host && formData.smtp?.user && formData.smtp?.password && formData.smtp?.fromEmail ? 'success' : 'warning'}`}>
                {formData.smtp?.enabled && formData.smtp?.host && formData.smtp?.user && formData.smtp?.password && formData.smtp?.fromEmail ? (
                  <>
                    <span className="status-icon">✓</span>
                    <div>
                      <strong>Email Service Active</strong>
                      <p>SMTP is configured. Emails will be sent for order confirmations and account creation.</p>
                    </div>
                  </>
                ) : (
                  <>
                    <span className="status-icon">⚠</span>
                    <div>
                      <strong>Email Service Not Configured</strong>
                      <p>Please configure SMTP settings below to enable email notifications.</p>
                    </div>
                  </>
                )}
              </div>

              {/* SMTP Configuration */}
              <div className="payment-gateway-section">
                <h4>⚙️ SMTP Server Settings</h4>
                
                <div className="form-group">
                  <label>
                    <input
                      type="checkbox"
                      name="enabled"
                      checked={formData.smtp?.enabled || false}
                      onChange={handleSmtpChange}
                    />
                    Enable Email Service
                  </label>
                  <small>Enable this to send automated emails for orders and account creation</small>
                </div>

                <div className="form-group">
                  <label htmlFor="smtpHost">
                    SMTP Host <span className="required">*</span>
                  </label>
                  <input
                    id="smtpHost"
                    type="text"
                    name="host"
                    value={formData.smtp?.host || ''}
                    onChange={handleSmtpChange}
                    placeholder="smtp.gmail.com"
                  />
                  <small>
                    SMTP server hostname (e.g., smtp.gmail.com, smtp.outlook.com, smtp.yahoo.com)
                  </small>
                </div>

                <div className="form-group">
                  <label htmlFor="smtpPort">
                    SMTP Port <span className="required">*</span>
                  </label>
                  <input
                    id="smtpPort"
                    type="number"
                    name="port"
                    value={formData.smtp?.port || 587}
                    onChange={handleSmtpChange}
                    placeholder="587"
                  />
                  <small>
                    SMTP port (587 for TLS, 465 for SSL, 25 for unencrypted)
                  </small>
                </div>

                <div className="form-group">
                  <label>
                    <input
                      type="checkbox"
                      name="secure"
                      checked={formData.smtp?.secure || false}
                      onChange={handleSmtpChange}
                    />
                    Use SSL/TLS (Port 465)
                  </label>
                  <small>Enable for port 465 (SSL), disable for port 587 (TLS)</small>
                </div>

                <div className="form-group">
                  <label htmlFor="smtpUser">
                    SMTP Username/Email <span className="required">*</span>
                  </label>
                  <input
                    id="smtpUser"
                    type="email"
                    name="user"
                    value={formData.smtp?.user || ''}
                    onChange={handleSmtpChange}
                    placeholder="your-email@gmail.com"
                  />
                  <small>
                    Your email address used for SMTP authentication
                  </small>
                </div>

                <div className="form-group">
                  <label htmlFor="smtpPassword">
                    SMTP Password/App Password <span className="required">*</span>
                  </label>
                  <div className="input-with-toggle">
                    <input
                      id="smtpPassword"
                      type="password"
                      name="password"
                      value={formData.smtp?.password || ''}
                      onChange={handleSmtpChange}
                      placeholder={formData.smtp?.password?.startsWith('••••') ? '••••••••' + formData.smtp.password.slice(-4) : 'Enter SMTP password or app password'}
                    />
                    {formData.smtp?.password?.startsWith('••••') && (
                      <span className="masked-indicator">Masked - Leave unchanged or enter new password</span>
                    )}
                  </div>
                  <small>
                    For Gmail: Use App Password (not your regular password). 
                    <a href="https://support.google.com/accounts/answer/185833" target="_blank" rel="noopener noreferrer"> Learn how to create one</a>
                  </small>
                </div>

                <div className="form-group">
                  <label htmlFor="smtpFromEmail">
                    From Email Address <span className="required">*</span>
                  </label>
                  <input
                    id="smtpFromEmail"
                    type="email"
                    name="fromEmail"
                    value={formData.smtp?.fromEmail || ''}
                    onChange={handleSmtpChange}
                    placeholder="noreply@shilpani.com"
                  />
                  <small>
                    Email address that will appear as the sender
                  </small>
                </div>

                <div className="form-group">
                  <label htmlFor="smtpFromName">
                    From Name
                  </label>
                  <input
                    id="smtpFromName"
                    type="text"
                    name="fromName"
                    value={formData.smtp?.fromName || 'Shilpani'}
                    onChange={handleSmtpChange}
                    placeholder="Shilpani"
                  />
                  <small>
                    Display name for the sender
                  </small>
                </div>

                <div className="info-box" style={{ marginTop: '20px', padding: '15px', background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: '5px' }}>
                  <h4 style={{ marginTop: 0 }}>📝 Common SMTP Settings:</h4>
                  <ul style={{ marginBottom: 0 }}>
                    <li><strong>Gmail:</strong> smtp.gmail.com, Port 587, Use App Password</li>
                    <li><strong>Outlook/Hotmail:</strong> smtp-mail.outlook.com, Port 587</li>
                    <li><strong>Yahoo:</strong> smtp.mail.yahoo.com, Port 587</li>
                    <li><strong>Custom SMTP:</strong> Check with your email provider</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Shipping Tab */}
        {activeTab === 'shipping' && (
          <div className="tab-content">
            {/* Shipping Origin Address */}
            <div className="form-section">
              <div className="section-header">
                <h4>Shipping Origin Address</h4>
                  name="street"
                  value={formData.shipping?.originAddress?.street || ''}
                  onChange={handleShippingOriginAddressChange}
                  placeholder="123 Warehouse Street"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>City</label>
                  <input
                    type="text"
                    name="city"
                    value={formData.shipping?.originAddress?.city || ''}
                    onChange={handleShippingOriginAddressChange}
                    placeholder="Mumbai"
                  />
                </div>

                <div className="form-group">
                  <label>State</label>
                  <input
                    type="text"
                    name="state"
                    value={formData.shipping?.originAddress?.state || ''}
                    onChange={handleShippingOriginAddressChange}
                    placeholder="Maharashtra"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Zip Code</label>
                  <input
                    type="text"
                    name="zipCode"
                    value={formData.shipping?.originAddress?.zipCode || ''}
                    onChange={handleShippingOriginAddressChange}
                    placeholder="400001"
                  />
                </div>

                <div className="form-group">
                  <label>Country</label>
                  <input
                    type="text"
                    name="country"
                    value={formData.shipping?.originAddress?.country || 'India'}
                    onChange={handleShippingOriginAddressChange}
                    placeholder="India"
                  />
                </div>
              </div>
            </div>

            {/* Shipping Pricing Rules */}
            <div className="form-section">
              <h3>Shipping Pricing Rules</h3>
              
              <div className="form-row">
                <div className="form-group">
                  <label>Base Shipping Price (₹)</label>
                  <input
                    type="number"
                    name="basePrice"
                    value={formData.shipping?.pricingRules?.basePrice || 100}
                    onChange={handleShippingPricingChange}
                    min="0"
                    step="0.01"
                  />
                  <small>Default shipping price if no other rules apply</small>
                </div>

                <div className="form-group">
                  <label>Free Shipping Threshold (₹)</label>
                  <input
                    type="number"
                    name="freeShippingThreshold"
                    value={formData.shipping?.pricingRules?.freeShippingThreshold || 5000}
                    onChange={handleShippingPricingChange}
                    min="0"
                    step="0.01"
                  />
                  <small>Orders above this amount get free shipping</small>
                </div>
              </div>

              {/* Weight-Based Pricing */}
              <div className="form-section-sub">
                <h4>Weight-Based Pricing</h4>
                <div className="form-group">
                  <label>
                    <input
                      type="checkbox"
                      name="weightBased.enabled"
                      checked={formData.shipping?.pricingRules?.weightBased?.enabled || false}
                      onChange={handleShippingPricingChange}
                    />
                    Enable weight-based pricing
                  </label>
                </div>

                {formData.shipping?.pricingRules?.weightBased?.enabled && (
                  <div className="form-row">
                    <div className="form-group">
                      <label>Price per Kg (₹)</label>
                      <input
                        type="number"
                        name="weightBased.pricePerKg"
                        value={formData.shipping?.pricingRules?.weightBased?.pricePerKg || 50}
                        onChange={handleShippingPricingChange}
                        min="0"
                        step="0.01"
                      />
                    </div>

                    <div className="form-group">
                      <label>Max Weight (kg)</label>
                      <input
                        type="number"
                        name="weightBased.maxWeight"
                        value={formData.shipping?.pricingRules?.weightBased?.maxWeight || 50}
                        onChange={handleShippingPricingChange}
                        min="0"
                        step="0.01"
                      />
                      <small>Maximum weight for shipping (beyond this, contact customer)</small>
                    </div>
                  </div>
                )}
              </div>

              {/* Size-Based Pricing */}
              <div className="form-section-sub">
                <h4>Size-Based Pricing</h4>
                <div className="form-group">
                  <label>
                    <input
                      type="checkbox"
                      name="sizeBased.enabled"
                      checked={formData.shipping?.pricingRules?.sizeBased?.enabled || false}
                      onChange={handleShippingPricingChange}
                    />
                    Enable size-based pricing (based on total dimensions: L + W + H)
                  </label>
                </div>

                {formData.shipping?.pricingRules?.sizeBased?.enabled && (
                  <div>
                    <div className="form-group">
                      <button
                        type="button"
                        onClick={handleSizeTierAdd}
                        className="btn-secondary"
                      >
                        + Add Size Tier
                      </button>
                    </div>

                    {formData.shipping?.pricingRules?.sizeBased?.tiers?.map((tier, index) => (
                      <div key={index} className="form-row" style={{ marginBottom: '10px', alignItems: 'flex-end' }}>
                        <div className="form-group">
                          <label>Max Total Dimension (cm)</label>
                          <input
                            type="number"
                            value={tier.maxDimension || 0}
                            onChange={(e) => handleSizeTierChange(index, 'maxDimension', e.target.value)}
                            min="0"
                            step="0.01"
                            placeholder="e.g., 150"
                          />
                          <small>L + W + H in cm</small>
                        </div>

                        <div className="form-group">
                          <label>Shipping Price (₹)</label>
                          <input
                            type="number"
                            value={tier.price || 0}
                            onChange={(e) => handleSizeTierChange(index, 'price', e.target.value)}
                            min="0"
                            step="0.01"
                            placeholder="e.g., 200"
                          />
                        </div>

                        <div className="form-group">
                          <button
                            type="button"
                            onClick={() => handleSizeTierRemove(index)}
                            className="btn-danger"
                            style={{ marginBottom: 0 }}
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    ))}

                    <small className="info-text">
                      Tiers are evaluated from smallest to largest. First matching tier applies.
                    </small>
                  </div>
                )}
              </div>
            </div>

            {/* Shipping Information */}
            <div className="form-section">
              <h3>Shipping Information (Displayed to Customers)</h3>
              
              <div className="form-group">
                <label>Estimated Delivery Time</label>
                <input
                  type="text"
                  name="estimatedDelivery"
                  value={formData.shipping?.information?.estimatedDelivery || '5-7 business days'}
                  onChange={handleShippingInfoChange}
                  placeholder="5-7 business days"
                />
              </div>

              <div className="form-group">
                <label>Processing Time</label>
                <input
                  type="text"
                  name="processingTime"
                  value={formData.shipping?.information?.processingTime || '1-2 business days'}
                  onChange={handleShippingInfoChange}
                  placeholder="1-2 business days"
                />
              </div>

              <div className="form-group">
                <label>Shipping Methods</label>
                <input
                  type="text"
                  name="shippingMethods"
                  value={formData.shipping?.information?.shippingMethods || 'Standard Shipping, Express Shipping'}
                  onChange={handleShippingInfoChange}
                  placeholder="Standard Shipping, Express Shipping"
                />
              </div>

              <div className="form-group">
                <label>Return Policy</label>
                <input
                  type="text"
                  name="returnPolicy"
                  value={formData.shipping?.information?.returnPolicy || '7 days return policy'}
                  onChange={handleShippingInfoChange}
                  placeholder="7 days return policy"
                />
              </div>

              <div className="form-group">
                <label>Care Instructions</label>
                <textarea
                  name="careInstructions"
                  value={formData.shipping?.information?.careInstructions || ''}
                  onChange={handleShippingInfoChange}
                  rows="3"
                  placeholder="Handle with care. Keep away from moisture."
                />
              </div>
            </div>
          </div>
        )}

        <div className="form-actions">
          <button type="submit" className="btn-primary">
            Save Settings
          </button>
        </div>
      </form>
    </div>
  );
};

export default SettingsManager;
